import { ConfirmModal } from "@/components/modal";
import { Typography } from "@/components/text";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import { genCaseTitle } from "@/libraries/helper";
import {
	getBulletinsMetaOptions,
	getCasesMetaQueryOptions,
	getGroupsMetaQueryOptions,
	getMemberChatsMetaOptions,
	getMyAlertsMetaQueryOptions,
	getRemindersMeta,
} from "@/queries";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { useQueries } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { orderBy } from "lodash";
import { Fragment, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface AppBarDrawerProps {
	container: Element;
	open: boolean;
	onClose: () => void;
}

const AppBarDrawer: React.FC<AppBarDrawerProps> = (props) => {
	const { container, open, onClose } = props;
	const location = useLocation();
	const [isOpenCreateCase, setOpenCreateCase] = useState(false);
	const navigate = useNavigate();
	const { profile } = useProfile();

	const modalConfirmRef = useRef<HTMLDivElement>(null);

	const [
		caseQuery,
		groupQuery,
		singleQuery,
		alertQuery,
		reminderQuery,
		bulletinsQuery,
	] = useQueries({
		queries: [
			getCasesMetaQueryOptions({
				status: "active",
			}),
			getGroupsMetaQueryOptions({
				status: "active",
			}),
			getMemberChatsMetaOptions({}),
			getMyAlertsMetaQueryOptions({
				scope: "my_outstanding",
			}),
			getRemindersMeta(),
			getBulletinsMetaOptions(),
		],
	});
	const activeCases = useMemo(
		() =>
			orderBy(
				caseQuery.data?.cases ?? [],
				(c) => c.conversation_updated_at,
				"desc",
			),
		[caseQuery.data],
	);
	const topCases = activeCases.filter((_, idx) => [0, 1, 2].includes(idx));
	const remainingCases = activeCases.filter(
		(_, idx) => ![0, 1, 2].includes(idx),
	);
	const groupCount = groupQuery.data?.meta?.total_count ?? 0;
	const singleCount = singleQuery.data?.meta?.total_count ?? 0;
	const isEmptyChat = !groupCount && !singleCount;
	const outstandingAlertCount = alertQuery.data?.meta?.total_count ?? 0;
	const reminderCount = reminderQuery.data?.meta?.total_count ?? 0;
	const isEmptyInbox = !outstandingAlertCount && !reminderCount;
	const bulletins = useMemo(
		() => orderBy(bulletinsQuery.data?.bulletins ?? [], "updated_at", "desc"),
		[bulletinsQuery.data?.bulletins],
	);
	const lastBulletin = bulletins.at(0);
	const isEmptyBulletin = !bulletins.length;

	useOnClickOutside(modalConfirmRef, () => setOpenCreateCase(false));

	return createPortal(
		<>
			<ConfirmModal
				ref={modalConfirmRef}
				open={isOpenCreateCase}
				onClose={() => setOpenCreateCase(false)}
				title="Add New Case"
				positiveButtonProps={{
					label: "New Patient",
					onClick: () => navigate({ to: "/dashboard/case/create" }),
				}}
				negativeButtonProps={{
					label: "Select Existing Patient",
					onClick: () => navigate({ to: "/dashboard/case/create/exist" }),
				}}
			/>
			{open ? (
				<div
					className="absolute inset-0 bg-black bg-opacity-40 z-40"
					onMouseDown={onClose}
				/>
			) : null}
			<div
				className={`absolute top-0 right-0 w-full md:w-10/12 h-dvh flex flex-col overflow-y-hidden bg-white shadow-lg z-50 transform transition-transform duration-300 ${
					open
						? "translate-x-0 opacity-100 visible"
						: "translate-x-full opacity-0 invisible"
				}`}
			>
				<div className="bg-gradient-account container grid grid-rows-2 items-center">
					<div className="h-12" />
					<div className="h-14 flex flex-row items-center justify-between">
						<Typography fontWeight={400} fontSize={20} className="text-white">
							Hello,{" "}
							<Typography component="span" fontWeight={700} fontSize={22}>
								{profile?.fullname}
							</Typography>
						</Typography>
						<IconButton color="primary" onClick={onClose}>
							<CloseIcon className="text-white" />
						</IconButton>
					</div>
				</div>
				<div className="h-full container py-4 flex flex-col gap-4 overflow-y-auto">
					<div className="shadow-card p-4 rounded-xl">
						<div className="flex flex-col gap-6 justify-center">
							<Typography fontSize={14} fontWeight={700}>
								CASES
							</Typography>
							{topCases ? (
								<>
									{topCases.map((activeCase) => (
										<Fragment key={activeCase.id}>
											<div className="flex flex-row gap-2 items-center justify-normal">
												<div className="flex-1">
													<Typography fontWeight={500} fontSize={14}>
														{genCaseTitle(
															activeCase.title,
															activeCase.patient?.name,
															activeCase.patient?.surname,
														)}
													</Typography>
													<Typography fontWeight={500} fontSize={12}>
														{[
															`${
																activeCase.patient?.dob
																	? dayjs().diff(
																			activeCase.patient.dob,
																			"years",
																		)
																	: activeCase.patient?.year_of_birth
																		? dayjs().year() -
																			activeCase.patient.year_of_birth
																		: "-"
															} yo`,
															activeCase.patient
																? (activeCase.title ?? "-")
																: (activeCase.summary ?? "-"),
														].join(" • ")}
													</Typography>
												</div>
												{activeCase.unread ? (
													<div className="w-fit px-2 h-6 flex justify-center items-center bg-secondary rounded-full">
														<Typography fontWeight={500} fontSize={14}>
															{activeCase.unread}
														</Typography>
													</div>
												) : null}
											</div>
											<Divider />
										</Fragment>
									))}
								</>
							) : (
								<Typography
									fontSize={14}
									fontWeight={700}
									className="text-zinc-500"
								>
									You have no existing cases
								</Typography>
							)}
							<div className="flex items-center justify-between">
								<div>
									{remainingCases.length ? (
										<Link
											to="/dashboard/case/$status"
											params={{
												status: "active",
											}}
										>
											<Typography
												component="span"
												fontWeight={600}
												fontSize={12}
												className="text-primary uppercase"
											>
												View More {remainingCases.length}{" "}
												{remainingCases.length > 1 ? "Cases" : "Case"}
											</Typography>
										</Link>
									) : null}
								</div>
								<Button
									variant="contained"
									color="primary"
									startIcon={<AddIcon />}
									className="!rounded-full"
									onClick={() => setOpenCreateCase(true)}
								>
									Add Case
								</Button>
							</div>
						</div>
					</div>
					<div className="shadow-card p-4 rounded-xl">
						<div className="flex flex-col gap-6 justify-center">
							<Typography fontSize={14} fontWeight={700}>
								CHAT
							</Typography>
							{!isEmptyChat ? (
								<Typography fontWeight={500} fontSize={14}>
									{[`${groupCount} Group`, `${singleCount} One to one`].join(
										", ",
									)}
								</Typography>
							) : null}
							{isEmptyChat ? (
								<Typography
									fontSize={14}
									fontWeight={700}
									className="text-zinc-500"
								>
									You have no existing chat
								</Typography>
							) : null}
							<div className="flex items-center justify-between">
								<div>
									{!isEmptyChat ? (
										<Link to="/dashboard/chat/single">
											<Typography
												component="span"
												fontWeight={600}
												fontSize={12}
												className="text-primary uppercase"
											>
												View All
											</Typography>
										</Link>
									) : null}
								</div>
								<Link to="/dashboard/chat/single">
									<Button
										variant="contained"
										color="primary"
										startIcon={<AddIcon />}
										className="!rounded-full"
									>
										New Chat
									</Button>
								</Link>
							</div>
						</div>
					</div>
					<div className="shadow-card p-4 rounded-xl">
						<div className="flex flex-col gap-6 justify-center">
							<Typography fontSize={14} fontWeight={700}>
								INBOX
							</Typography>
							{!isEmptyChat ? (
								<Typography fontWeight={500} fontSize={14}>
									{[
										`${outstandingAlertCount} Outstanding`,
										`${reminderCount} Reminders`,
									].join(", ")}
								</Typography>
							) : null}
							{isEmptyInbox ? (
								<Typography
									fontSize={14}
									fontWeight={700}
									className="text-zinc-500"
								>
									You have no inbox entry
								</Typography>
							) : null}
							<div className="flex items-center justify-between">
								<div>
									{!isEmptyInbox ? (
										<Link to="/dashboard/inbox/alerts">
											<Typography
												component="span"
												fontWeight={600}
												fontSize={12}
												className="text-primary uppercase"
											>
												View All
											</Typography>
										</Link>
									) : null}
								</div>
								<Link to="/dashboard/inbox/reminders/create">
									<Button
										variant="contained"
										color="primary"
										startIcon={<AddIcon />}
										className="!rounded-full"
									>
										Add Reminder
									</Button>
								</Link>
							</div>
						</div>
					</div>
					<div className="shadow-card p-4 rounded-xl">
						<div className="flex flex-col gap-6 justify-center">
							<Typography fontSize={14} fontWeight={700}>
								BULLETIN
							</Typography>
							{lastBulletin ? (
								<>
									<div className="flex flex-row gap-2 items-center justify-normal">
										<div className="flex-1 flex flex-col gap-1 justify-center">
											{lastBulletin.title ? (
												<Typography
													fontWeight={600}
													fontSize={12}
													className="line-clamp-1 text-primary"
												>
													{lastBulletin.title}
												</Typography>
											) : null}
											{lastBulletin.description ? (
												<Typography
													fontWeight={400}
													fontSize={12}
													className="line-clamp-3"
												>
													{lastBulletin.description}
												</Typography>
											) : null}
											{lastBulletin.link ? (
												<a
													href={lastBulletin.link}
													className="text-primary"
													target="_blank"
													rel="noopener noreferrer"
												>
													<Typography
														component="span"
														fontWeight={400}
														fontSize={12}
													>
														{lastBulletin.link}
													</Typography>
												</a>
											) : null}
										</div>
										{lastBulletin.image_url ? (
											<img
												src={lastBulletin.image_url}
												alt={
													lastBulletin.title ??
													lastBulletin.description ??
													lastBulletin.image_url
												}
												className="w-20 h-20 rounded-xl"
											/>
										) : null}
									</div>
									<Typography
										fontWeight={400}
										fontSize={12}
										className="text-pretty line-clamp-1 w-20 text-center"
									>
										{dayjs(lastBulletin.updated_at).format("DD MMM YYYY")}
									</Typography>
									<Divider />
								</>
							) : null}
							{isEmptyBulletin ? (
								<Typography
									fontSize={14}
									fontWeight={700}
									className="text-zinc-500"
								>
									You have no existing bulletin
								</Typography>
							) : null}
							<div className="flex items-center justify-between">
								<div>
									{!isEmptyBulletin ? (
										<Link to="/dashboard/bulletin">
											<Typography
												component="span"
												fontWeight={600}
												fontSize={12}
												className="text-primary uppercase"
											>
												View All
											</Typography>
										</Link>
									) : null}
								</div>
								<Link to="/dashboard/bulletin/setting">
									<Button
										variant="contained"
										color="primary"
										startIcon={<AddIcon />}
										className="!rounded-full"
									>
										Add Bulletin
									</Button>
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>,
		container,
	);
};

export default AppBarDrawer;
